import { Loading } from '@/components/shared';
import { FileInfo, ReferenceFile, UploadedFile } from "@/components/shared/file-upload/types";
import {
  useDirectScheduleAdjustmentQuery,
  useUpdateDirectScheduleAdjustmentMutation} from '@/hooks/apis';
import { useGetS3UploadParamsMutation, useS3UploadMutation } from "@/hooks/apis/directScheduleAdjustments/useS3UploadMutation";
import { Order } from '@/types';
import { bem } from '@/utilities';
import { calculateFileMD5 } from '@/utils/md5';
import dayjs from 'dayjs';
import ja from 'dayjs/locale/ja';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FlashContext } from "@/providers";
import { AddressForm } from './addressForm';
import { LoadingAdjustmentModal } from './loadingAdjustmentModal';
import { OrderCompletedModal } from './orderCompletedModal';
import { ScheduleTimeframe } from './scheduleTimeframe';
import { SearchFieldMobile } from './searchFieldMobile';
import { Sidebar } from './sidebar';

dayjs.locale(ja);

const displayLoadingModalSeconds = 2;

type OpeningModal = 'request' | 'loading' | 'completed';

type DeadlineViewProps = {
  assignableStartDate?: string;
  assignableEndDate?: string;
  bookingDeadline?: string;
};
const DeadlineView: React.FC<DeadlineViewProps> = (props) => {
  const { assignableStartDate, assignableEndDate, bookingDeadline } = props;
  const today = dayjs(dayjs().format('YYYY-MM-DD'));
  const remainingDay = dayjs(bookingDeadline).diff(today, 'day');

  return (
    <div className={bem('schedule-selection', 'deadline')}>
      <div className={bem('schedule-selection', 'deadline-main')}>
        <p className={bem('schedule-selection', 'deadline-main__title')}>作業期間</p>
        <div className={bem('schedule-selection', 'deadline-main__inner')}>
          <p className={bem('schedule-selection', 'deadline-main__time')}>
            {dayjs(assignableStartDate).format('YYYY年MM月DD日（dd）')}〜
            {dayjs(assignableEndDate).format('YYYY年MM月DD日（dd）')}
          </p>
        </div>
      </div>
      <hr className={bem('schedule-selection', 'deadline-hr')} />
      {bookingDeadline && (
        <>
          <div className={bem('schedule-selection', 'deadline-sub')}>
            <p className={bem('schedule-selection', 'deadline-sub__text')}>
              ご予約の確定は{' '}
              <span className={bem('schedule-selection', 'deadline-sub__text-strong')}>
                {dayjs(bookingDeadline).format('YYYY年MM月DD日（dd）23:59まで ')}
              </span>
              受け付けています
            </p>
            {remainingDay >= 0 && remainingDay <= 5 && (
              <span className={bem('schedule-selection', 'deadline-sub__remaining')}>
                {remainingDay === 0 ? '今日中' : `残り${remainingDay}日`}
              </span>
            )}
          </div>
          <p className={bem('schedule-selection', 'deadline-notes')}>
            <span className={bem('schedule-selection', 'deadline-notes__strong')}>下記車両情報を必ずご確認</span>
            の上、予約期間内に
            <span className={bem('schedule-selection', 'deadline-notes__strong')}>予約を必ず完了</span>
            してください。
            <br />
            車両情報に相違がある場合や、期限内に予約ができない場合は、お問い合わせフォームでご連絡ください。
          </p>
        </>
      )}
    </div>
  );
};

type NumbersViewProps = {
  unassignedOrdersCount: number;
  scheduledOrdersCount: number;
};

const NumbersView: React.FC<NumbersViewProps> = (props) => {
  const { unassignedOrdersCount, scheduledOrdersCount } = props;

  return (
    <div className={bem('schedule-selection', 'numbers')}>
      <div className={bem('schedule-selection', 'numbers-pending')}>
        <span className={bem('schedule-selection', 'numbers-pending__title')}>作業未確定</span>
        <span className={bem('schedule-selection', 'numbers-pending__number')}>{unassignedOrdersCount}</span>
        <span className={bem('schedule-selection', 'numbers-pending__unit')}>件</span>
      </div>
      <div className={bem('schedule-selection', 'numbers-schedule')}>
        <span className={bem('schedule-selection', 'numbers-schedule__title')}>これからの作業予定</span>
        <span className={bem('schedule-selection', 'numbers-schedule__number')}>{scheduledOrdersCount}</span>
        <span className={bem('schedule-selection', 'numbers-schedule__unit')}>件</span>
      </div>
    </div>
  );
};

type OrderDetailViewProps = {
  selectedOrder?: Order;
  resetSelectedOrder: () => void;
};

const OrderDetailView: React.FC<OrderDetailViewProps> = (props) => {
  const { selectedOrder, resetSelectedOrder } = props;

  const formattedOperationDurationRange = (minutes: number): string => {
    // 最大で作業時間の130%までの時間を表示
    const maximumOperationMinutesFactor = 1.3;

    // 時間は5分単位で切り上げて表示
    const normalOperationMinutes = Math.ceil(minutes / 5) * 5;
    const maximumOperationMinutes = Math.ceil((normalOperationMinutes * maximumOperationMinutesFactor) / 5) * 5;

    return `${normalOperationMinutes}~${maximumOperationMinutes}`;
  };

  return (
    <div className={bem('schedule-selection', 'order-detail')}>
      {selectedOrder ? (
        <>
          <div className={bem('schedule-selection', 'order-detail__close')} onClick={() => resetSelectedOrder()} />
          <table className={bem('schedule-selection', 'order-detail__info')}>
            <tbody>
              <tr>
                <td valign='top'>
                  <p className={bem('schedule-selection', 'order-detail__car-label')}>車両</p>
                </td>
                <td>
                  {selectedOrder.carManagementNumber && (
                    <p className={bem('schedule-selection', 'order-detail__car-text')}>
                      {selectedOrder.carManagementNumber}
                    </p>
                  )}
                  <p className={bem('schedule-selection', 'order-detail__car-text')}>
                    {selectedOrder.ownedCar.carNumber}
                  </p>
                  {selectedOrder.ownedCar.vehicleNumber && (
                    <p className={bem('schedule-selection', 'order-detail__car-text')}>
                      {selectedOrder.ownedCar.vehicleNumber}
                    </p>
                  )}
                  <p className={bem('schedule-selection', 'order-detail__car-text')}>
                    <span>{selectedOrder.ownedCar.carModelName}</span>
                    <span>{selectedOrder.ownedCar.carModelNumber}</span>
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p className={bem('schedule-selection', 'order-detail__name-label')}>名前</p>
                </td>
                <td>
                  <p
                    className={bem('schedule-selection', 'order-detail__name-text')}
                  >{`${selectedOrder.userName}様`}</p>
                </td>
              </tr>
              <tr>
                <td>
                  <p className={bem('schedule-selection', 'order-detail__location-label')}>作業場所住所</p>
                </td>
                <td>
                  <p className={bem('schedule-selection', 'order-detail__location-text')}>{selectedOrder.location}</p>
                </td>
              </tr>
              <tr>
                <td>
                  <p className={bem('schedule-selection', 'order-detail__time-label')}>作業目安</p>
                </td>
                <td>
                  <p
                    className={bem('schedule-selection', 'order-detail__time-text')}
                  >{`${formattedOperationDurationRange(
                    selectedOrder.duration
                  )}分 ※車種や車両の状態により作業時間が前後する可能性がございます`}</p>
                </td>
              </tr>
              {selectedOrder?.devices?.length > 0 && (
                <tr>
                  <td>
                    <p className={bem('schedule-selection', 'order-detail__memo-label')}>取付機器</p>
                  </td>
                  <td>
                    <p className={bem('schedule-selection', 'order-detail__memo-text')}>
                      {selectedOrder.devices.some((device) => device.hasCustomerDirectDelivery)
                        ? '作業日までにお手元に届きますので当日整備士にお渡しください'
                        : '作業当日に担当整備士がお持ちし作業いたします'}
                    </p>
                  </td>
                </tr>
              )}
              {!!selectedOrder.directScheduleCustomerMemo && (
                <tr>
                  <td>
                    <p className={bem('schedule-selection', 'order-detail__memo-label')}>ご連絡事項</p>
                  </td>
                  <td>
                    <p
                      className={bem('schedule-selection', 'order-detail__memo-text')}
                    >{`${selectedOrder.directScheduleCustomerMemo}`}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      ) : (
        <p className={bem('schedule-selection', 'order-detail__empty')}>車両を選択してください</p>
      )}
    </div>
  );
};

export const ScheduleSelections: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const selectOrderId = query.get('select_order_id');
  const hasUpdateError = query.get('has_error');
  const today = new Date();
  const { token } = useParams();
  const { setFlash } = useContext(FlashContext);
  const directScheduleAdjustmentQuery = useDirectScheduleAdjustmentQuery(token);
  const updateDirectScheduleAdjustment = useUpdateDirectScheduleAdjustmentMutation(token);
  const getS3UploadParamsMutation = useGetS3UploadParamsMutation(token);
  const s3UploadMutation = useS3UploadMutation();
  const [orderScheduledAt, setOrderScheduledAt] = useState<string>();
  const [openingModal, setOpeningModal] = useState<OpeningModal | null>();
  const [isAddressConfirmed, setIsAddressConfirmed] = useState<boolean>(false);
  let isSuccessSubmit = false;
  let isOverLoadingTime = false;

  const orders = directScheduleAdjustmentQuery.data?.orders;
  const assignableOrders = orders?.filter((order) => !order.scheduledAt) || [];
  const selectedOrder = assignableOrders.find((order) => order.id === Number(selectOrderId));

  useEffect(() => {
    setIsAddressConfirmed(false);
  }, [selectedOrder?.id]);

  const setSelectedOrder = (order: Order) => {
    navigate(`/schedule_adjustments/${token}?select_order_id=${order.id}`);
  };

  const resetSelectedOrder = () => {
    navigate(`/schedule_adjustments/${token}`);
  };

  const setDisplayLoadingModalTime = () => {
    window.setTimeout(() => {
      isOverLoadingTime = true;
      openCompletedModal();
    }, displayLoadingModalSeconds * 1000);
  };

  const openCompletedModal = () => {
    if (!isSuccessSubmit || !isOverLoadingTime) return;
    setOpeningModal('completed');
  };

  const handleClickSubmitButton = async (
    contactName: string,
    contactPhoneNumber: string,
    parkingType: string,
    comment: string,
    selectedAvailableTimeframeStart: string,
    selectedAvailableTimeframeEnd: string,
    uploadedFiles: UploadedFile[]
  ) => {
    setOpeningModal('loading');
    setDisplayLoadingModalTime();

    try {
      const referenceFiles: ReferenceFile[] = [];

      if (uploadedFiles.length > 0) {
        // 各ファイルのMD5チェックサムを計算
        const fileInfo: FileInfo[] = await Promise.all(
          uploadedFiles.map(async uploadedFile => {
            const checksum = await calculateFileMD5(uploadedFile.file);
            return {
              content_type: uploadedFile.file.type,
              file_name: uploadedFile.file.name,
              file_size: uploadedFile.file.size,
              checksum: checksum,
            };
          })
        );

        // 複数ファイルのS3アップロードパラメータを一括取得
        const uploadParamsResponse = await getS3UploadParamsMutation.mutateAsync({
          files: fileInfo,
        });

        // 各ファイルを対応するpresigned requestでアップロード
        for (let i = 0; i < uploadedFiles.length; i++) {
          const uploadedFile = uploadedFiles[i];
          const uploadParams = uploadParamsResponse.presigned_requests[i];

          await s3UploadMutation.mutateAsync({
            file: uploadedFile.file,
            uploadParams,
            contentMd5: fileInfo[i].checksum,
          });

          referenceFiles.push({
            file_key: uploadParams.file_key,
          });
        }
      }

      updateDirectScheduleAdjustment.mutate(
        {
          orderId: selectOrderId,
          contactName,
          contactPhoneNumber,
          mechanicParkingPlace:
            parkingType === 'emptySpace'
              ? '作業車両付近の空きスペース'
              : 'コインパーキング利用',
          comment,
          preferredTimeframeStart: selectedAvailableTimeframeStart,
          preferredTimeframeEnd: selectedAvailableTimeframeEnd,
          referenceFiles,
        },
      {
        onSuccess: ({ data }: { data: { scheduled_at: string } }) => {
          setOrderScheduledAt(data.scheduled_at);
          isSuccessSubmit = true;
          openCompletedModal();
        },
        onError: () => {
          window.location.href = `/schedule_adjustments/${token}?select_order_id=${selectOrderId}&has_error=true`;
        },
        onSettled: () => {
          window.scrollTo({ top: 0 });
        }
      }
      );
    } catch {
      setFlash({
        type: 'error',
        message: <>ファイルのアップロードに失敗しました。再度お試しください。</>,
      });
      setOpeningModal(null);
    }
  };

  if (!directScheduleAdjustmentQuery.isFetched) {
    return <Loading />;
  }

  return (
    <div className='schedule-selection'>
      <Sidebar
        orders={assignableOrders}
        selectedOrder={selectedOrder}
        setSelectedOrder={(selectedOrder: Order) => setSelectedOrder(selectedOrder)}
      />
      <div className={bem('schedule-selection', 'main')}>
        <SearchFieldMobile
          orders={assignableOrders}
          selectedOrder={selectedOrder}
          setSelectedOrder={(selectedOrder: Order) => setSelectedOrder(selectedOrder)}
        />
        <DeadlineView
          assignableStartDate={selectedOrder?.assignableStartDate}
          assignableEndDate={selectedOrder?.assignableEndDate}
          bookingDeadline={selectedOrder?.bookingDeadline}
        />
        <NumbersView
          unassignedOrdersCount={assignableOrders.length}
          scheduledOrdersCount={
            directScheduleAdjustmentQuery.data?.orders?.filter((order) => new Date(order.scheduledAt) > today)
              ?.length || 0
          }
        />
        {!!hasUpdateError && (
          <p className={bem('schedule-selection', 'error-text')}>
            サーバーのエラーが発生しました。お手数お掛けしますが、再度予約しなおしてください。
          </p>
        )}
        <OrderDetailView selectedOrder={selectedOrder} resetSelectedOrder={() => resetSelectedOrder()} />
        {selectedOrder && (
          <>
            <AddressForm
              key={selectedOrder.id}
              order={selectedOrder}
              token={token}
              onConfirmAddress={() => setIsAddressConfirmed(true)}
            />
            {isAddressConfirmed && (
              <ScheduleTimeframe
                token={token}
                orderId={selectedOrder.id}
                openOrderRequestModal={openingModal == 'request'}
                orders={directScheduleAdjustmentQuery.data?.orders || []}
                setOpenOrderRequestModal={(openOrderRequestModal: boolean) =>
                  openOrderRequestModal ? setOpeningModal('request') : setOpeningModal(null)
                }
                onClickSubmitButton={(
                  contactName: string,
                  contactPhoneNumber: string,
                  parkingType: string,
                  comment: string,
                  selectedAvailableTimeframeStart: string,
                  selectedAvailableTimeframeEnd: string,
                  uploadedFiles: UploadedFile[]
                ) =>
                  handleClickSubmitButton(
                    contactName,
                    contactPhoneNumber,
                    parkingType,
                    comment,
                    selectedAvailableTimeframeStart,
                    selectedAvailableTimeframeEnd,
                    uploadedFiles
                  )
                }
              />
            )}
          </>
        )}
        <LoadingAdjustmentModal openModal={openingModal == 'loading'} />
        <OrderCompletedModal
          openModal={openingModal == 'completed'}
          orderScheduledAt={orderScheduledAt}
          duration={orders?.find((order) => order.id === Number(selectOrderId))?.duration}
          ordersLink={`/orders/${token}`}
          onClickCloseModal={() => {
            setOpeningModal(null);
            navigate(`/schedule_adjustments/${token}`);
          }}
        />
      </div>
    </div>
  );
};
